/**
 * UCC Filings Hooks
 *
 * React Query hooks for UCC filing lifecycle operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/ucc-filing-lifecycle';
import {
  uccFilingLifecycleClient,
} from '../api/typed-clients';

/** Request body of POST /ucc-filings (schema-registry/ucc-filing-lifecycle.openapi.yaml). */
export type CreateFilingRequest = components['schemas']['CreateFilingRequest'];
/** Request body of POST /ucc-filings/{filingId}/transitions: `toStatus` and `initiatedBy` are required. */
export type TransitionFilingRequest = components['schemas']['TransitionRequest'];
import { apiErrorMessage } from '../api/error-message';

const FILINGS_KEY = 'uccFilings';
const FILING_KEY = 'uccFiling';
const FILING_EVENTS_KEY = 'uccFilingEvents';
const FILING_CHAIN_KEY = 'uccFilingChain';

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string } | undefined;
    throw new Error(apiErrorMessage(errorBody) ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

export function useFilings(params: { tenantId: string; status?: string; jurisdictionCode?: string; filingType?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: [FILINGS_KEY, params],
    queryFn: async () => {
      const result = await uccFilingLifecycleClient.GET('/ucc-filings', {
        params: {
          query: {
            tenantId: params.tenantId,
            status: params.status as never,
            jurisdictionCode: params.jurisdictionCode,
            filingType: params.filingType as never,
            page: params.page,
            limit: params.limit,
          },
        },
      });
      return unwrap(result);
    },
    enabled: !!params.tenantId,
  });
}

export function useFiling(filingId: string | undefined) {
  return useQuery({
    queryKey: [FILING_KEY, filingId],
    queryFn: async () => {
      const result = await uccFilingLifecycleClient.GET('/ucc-filings/{filingId}', {
        params: { path: { filingId: filingId! } },
      });
      return unwrap(result);
    },
    enabled: !!filingId,
  });
}

export function useFilingEvents(filingId: string | undefined) {
  return useQuery({
    queryKey: [FILING_EVENTS_KEY, filingId],
    queryFn: async () => {
      const result = await uccFilingLifecycleClient.GET('/ucc-filings/{filingId}/events', {
        params: { path: { filingId: filingId! } },
      });
      return unwrap(result);
    },
    enabled: !!filingId,
  });
}

export function useFilingChain(filingId: string | undefined) {
  return useQuery({
    queryKey: [FILING_CHAIN_KEY, filingId],
    queryFn: async () => {
      const result = await uccFilingLifecycleClient.GET('/ucc-filings/{filingId}/chain', {
        params: { path: { filingId: filingId! } },
      });
      return unwrap(result);
    },
    enabled: !!filingId,
  });
}

export function useCreateFiling() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateFilingRequest) => {
      const result = await uccFilingLifecycleClient.POST('/ucc-filings', { body });
      return unwrap(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FILINGS_KEY] });
    },
  });
}

export function useTransitionFiling() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ filingId, body }: { filingId: string; body: TransitionFilingRequest }) => {
      const result = await uccFilingLifecycleClient.POST('/ucc-filings/{filingId}/transitions', {
        params: { path: { filingId } },
        body,
      });
      return unwrap(result);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [FILINGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [FILING_KEY, variables.filingId] });
      queryClient.invalidateQueries({ queryKey: [FILING_EVENTS_KEY, variables.filingId] });
      queryClient.invalidateQueries({ queryKey: [FILING_CHAIN_KEY, variables.filingId] });
    },
  });
}
